import { Link } from "react-router";
import { JsonLdScript, type JsonLdValue } from "../components/JsonLdScript";
import { StickyMobileCTA } from "../components/StickyMobileCTA";
import type { Market } from "../domain/location/types";
import {
	SQUARE_DEFAULT_DIMENSIONS,
	TWO_ROOM_DEFAULT_DIMENSIONS,
} from "../engines/defaultDimensions";
import type { SaunaDimensions } from "../engines/types";
import { useBaseUrl, useOrgName } from "../hooks/useBaseUrl";
import { useMarket, useT } from "../i18n";
import { formatPrice } from "../i18n/currency";
import { COST_FACTS } from "./articles/intlGuides/costFacts";
import { ReferenceDesignCards } from "./articles/ReferenceDesignCards";
import { FILM_FACTS, FILM_FRAME_COUNT } from "./landing/filmFacts";
import { ScrollFilm } from "./landing/ScrollFilm";
import "./landing/landing.css";

interface LandingPageContentProps {
	showDashboardCta?: boolean;
}

function formatFootprint(dims: SaunaDimensions, locale: string): string {
	const fmt = new Intl.NumberFormat(locale, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	});
	return `${fmt.format(dims.width)} × ${fmt.format(dims.length)} m`;
}

const filmFrameUrl = (index: number) =>
	`/assets/landing/film/f${String(index).padStart(3, "0")}.webp`;

/** Fill `{key}` slots in a template, setting each figure apart. */
function withFigures(template: string, figures: Record<string, string>) {
	return template.split(/(\{\w+\})/).map((part, i) => {
		const key = /^\{(\w+)\}$/.exec(part)?.[1];
		return key && key in figures ? (
			// biome-ignore lint/suspicious/noArrayIndexKey: static template order
			<span key={i} className="sky-figure">
				{figures[key]}
			</span>
		) : (
			part
		);
	});
}

/**
 * The cost answer, filled with what the planner prices for its two opening
 * drafts (pinned by `costFacts.test.tsx`), so the FAQ cannot quote a range
 * the planner's own totals fall outside.
 */
function landingCostAnswer(
	template: string,
	market: Market,
	presetTitle: string,
): string {
	const { oneRoom, lakeside } = COST_FACTS;
	const metres = new Intl.NumberFormat(market === "se" ? "sv-SE" : "en-GB", {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	});
	const size = (d: { widthMm: number; lengthMm: number }) =>
		`${metres.format(d.widthMm / 1000)} × ${metres.format(d.lengthMm / 1000)}`;
	return template
		.replace("{oneRoomSize}", size(oneRoom))
		.replace("{oneRoomPrice}", formatPrice(oneRoom.materialCostSek, market))
		.replace("{preset}", presetTitle)
		.replace("{lakesideSize}", size(lakeside))
		.replace("{lakesidePrice}", formatPrice(lakeside.materialCostSek, market))
		.replace("{heaterBlock}", formatPrice(oneRoom.bySek.heater, market));
}

export function LandingPageContent({
	showDashboardCta = false,
}: LandingPageContentProps) {
	const t = useT();
	const market = useMarket();
	const baseUrl = useBaseUrl();
	const orgName = useOrgName();
	const costAnswer = landingCostAnswer(
		t.landing_faq1A,
		market,
		t.twoRoom_presetLakesideTitle,
	);
	const faqJsonLd: JsonLdValue = {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: [
			{
				"@type": "Question",
				name: t.landing_faq1Q,
				acceptedAnswer: { "@type": "Answer", text: costAnswer },
			},
			{
				"@type": "Question",
				name: t.landing_faq2Q,
				acceptedAnswer: { "@type": "Answer", text: t.landing_faq2A },
			},
			{
				"@type": "Question",
				name: t.landing_faq3Q,
				acceptedAnswer: { "@type": "Answer", text: t.landing_faq3A },
			},
			{
				"@type": "Question",
				name: t.landing_faq4Q,
				acceptedAnswer: { "@type": "Answer", text: t.landing_faq4A },
			},
			{
				"@type": "Question",
				name: t.landing_faq5Q,
				acceptedAnswer: { "@type": "Answer", text: t.landing_faq5A },
			},
		],
	};
	const productJsonLd: JsonLdValue = {
		"@context": "https://schema.org",
		"@type": "ItemList",
		name:
			market === "se"
				? "Bastutyper i Bygg en bastu"
				: "Sauna types in Sauna Creator",
		itemListElement: [
			{
				"@type": "ListItem",
				position: 1,
				item: {
					"@type": "Product",
					name: t.landing_typeSquare,
					description:
						"Configurable rectangular outdoor sauna with material takeoff and build guide.",
					url: `${baseUrl}/wizard?type=fyrkantig`,
					brand: { "@type": "Brand", name: orgName },
					category: "Outdoor sauna",
				},
			},
			{
				"@type": "ListItem",
				position: 2,
				item: {
					"@type": "Product",
					name: t.landing_typeTwoRoom,
					description:
						"Configurable two-room sauna with hot room, changing room, and panorama options.",
					url: `${baseUrl}/wizard?type=tvarum`,
					brand: { "@type": "Brand", name: orgName },
					category: "Outdoor sauna",
				},
			},
		],
	};

	const locale = market === "se" ? "sv-SE" : "en-GB";
	const types = [
		{
			to: "/wizard?type=fyrkantig",
			name: t.landing_typeSquare,
			desc: t.landing_typeSquareDesc,
			dims: SQUARE_DEFAULT_DIMENSIONS,
		},
		{
			to: "/wizard?type=tvarum",
			name: t.landing_typeTwoRoom,
			desc: t.landing_typeTwoRoomDesc,
			dims: TWO_ROOM_DEFAULT_DIMENSIONS,
		},
	];
	const outputs = [
		{ label: t.landing_feature3d, desc: t.landing_output3dDesc },
		{ label: t.landing_featureMaterials, desc: t.landing_outputMaterialsDesc },
		{ label: t.landing_featureCutlist, desc: t.landing_outputCutlistDesc },
		{ label: t.landing_featureRules, desc: t.landing_outputRulesDesc },
	];

	const sizeFmt = new Intl.NumberFormat(locale);
	const filmSize = `${sizeFmt.format(FILM_FACTS.widthMm)} × ${sizeFmt.format(FILM_FACTS.lengthMm)}`;
	const primaryCta = showDashboardCta ? (
		<Link to="/dashboard" className="sky-btn">
			{t.landing_goToProjects}
		</Link>
	) : (
		<Link to="/wizard" className="sky-btn">
			{t.landing_primaryCta}
		</Link>
	);

	return (
		<div className="landing sky">
			<ScrollFilm frameUrl={filmFrameUrl} frameCount={FILM_FRAME_COUNT}>
				<section className="sky-chapter sky-chapter--open">
					<h1 className="sky-title">{t.landing_title}</h1>
					<p className="sky-lede">{t.landing_subtitle}</p>
					<div className="sky-cta-row">
						{primaryCta}
						{!showDashboardCta && (
							<span className="sky-cta-note">{t.landing_ctaNote}</span>
						)}
					</div>
				</section>
				<section className="sky-chapter">
					<h2 className="sky-chapter-title">
						{withFigures(t.landing_filmPlanTitle, { size: filmSize })}
					</h2>
					<p className="sky-lede">
						{t.landing_filmPlanBody.replace(
							"{preset}",
							t.twoRoom_presetLakesideTitle,
						)}
					</p>
				</section>
				<section className="sky-chapter">
					<h2 className="sky-chapter-title">
						{withFigures(t.landing_filmListsTitle, {
							rows: String(FILM_FACTS.purchaseRows),
							cuts: String(FILM_FACTS.cutListRows),
						})}
					</h2>
					<p className="sky-lede">{t.landing_filmListsBody}</p>
				</section>
				<section className="sky-chapter">
					<h2 className="sky-chapter-title">
						{withFigures(t.landing_filmPriceTitle, {
							price: formatPrice(FILM_FACTS.materialCostSek, market),
						})}
					</h2>
					<p className="sky-lede">{t.landing_filmPriceBody}</p>
					<div className="sky-cta-row">
						<Link
							to={showDashboardCta ? "/dashboard" : "/wizard"}
							className="sky-btn"
						>
							{showDashboardCta
								? t.landing_goToProjects
								: t.landing_filmPriceCta}
						</Link>
					</div>
				</section>
			</ScrollFilm>

			<section className="sky-steps" aria-labelledby="steps-heading">
				<h2 id="steps-heading" className="sky-h2">
					{t.landing_stepsTitle}
				</h2>
				<ol className="sky-step-list">
					<li className="sky-step">
						<h3>{t.landing_step1Title}</h3>
						<p>{t.landing_step1Body}</p>
						<ul className="sky-types">
							{types.map((type) => (
								<li key={type.to}>
									<Link to={type.to} className="sky-type">
										<span className="sky-type-name">{type.name}</span>
										<span className="sky-type-desc">{type.desc}</span>
										<span className="sky-type-dims">
											{formatFootprint(type.dims, locale)}
										</span>
									</Link>
								</li>
							))}
						</ul>
					</li>
					<li className="sky-step">
						<h3>{t.landing_step2Title}</h3>
						<p>{t.landing_step2Body}</p>
					</li>
					<li className="sky-step">
						<h3>{t.landing_step3Title}</h3>
						<p>{t.landing_step3Body}</p>
					</li>
				</ol>
			</section>

			<section className="sky-outputs" aria-labelledby="outputs-heading">
				<h2 id="outputs-heading" className="sky-h2">
					{t.landing_outputsTitle}
				</h2>
				<dl className="sky-spec">
					{outputs.map((row) => (
						<div key={row.label} className="sky-spec-row">
							<dt>{row.label}</dt>
							<dd>{row.desc}</dd>
						</div>
					))}
				</dl>
			</section>

			{market === "se" && (
				<ReferenceDesignCards
					title={t.landing_referenceDesignsTitle}
					intro={t.landing_referenceDesignsIntro}
				/>
			)}

			<section className="landing-guides" aria-labelledby="guides-heading">
				<h2 id="guides-heading" className="sky-h2">
					{t.landing_guidesTitle}
				</h2>
				<p className="landing-guides-intro">{t.landing_guidesIntro}</p>
				<div className="landing-guide-grid">
					<Link
						to={market === "se" ? "/guide/bygga-bastu" : "/guide/build-a-sauna"}
						className="landing-guide-link"
					>
						<span className="landing-guide-title">
							{t.landing_guideBuildTitle}
						</span>
						<span className="landing-guide-blurb">
							{t.landing_guideBuildBlurb}
						</span>
					</Link>
					<Link
						to={market === "se" ? "/guide/bastu-kostnad" : "/guide/sauna-cost"}
						className="landing-guide-link"
					>
						<span className="landing-guide-title">
							{t.landing_guideCostTitle}
						</span>
						<span className="landing-guide-blurb">
							{t.landing_guideCostBlurb}
						</span>
					</Link>
					<Link
						to={
							market === "se" ? "/guide/bygglov-bastu" : "/guide/sauna-permits"
						}
						className="landing-guide-link"
					>
						<span className="landing-guide-title">
							{t.landing_guidePermitTitle}
						</span>
						<span className="landing-guide-blurb">
							{t.landing_guidePermitBlurb}
						</span>
					</Link>
					{market === "se" && (
						<>
							<Link to="/guide/bastupanel" className="landing-guide-link">
								<span className="landing-guide-title">
									{t.landing_guidePanelTitle}
								</span>
								<span className="landing-guide-blurb">
									{t.landing_guidePanelBlurb}
								</span>
							</Link>
							<Link
								to="/guide/ventilation-bastu"
								className="landing-guide-link"
							>
								<span className="landing-guide-title">
									{t.landing_guideVentTitle}
								</span>
								<span className="landing-guide-blurb">
									{t.landing_guideVentBlurb}
								</span>
							</Link>
						</>
					)}
					{market === "intl" && (
						<Link to="/guide/sauna-ventilation" className="landing-guide-link">
							<span className="landing-guide-title">
								{t.landing_guideVentTitle}
							</span>
							<span className="landing-guide-blurb">
								{t.landing_guideVentBlurb}
							</span>
						</Link>
					)}
					<Link
						to={
							market === "se"
								? "/guide/elaggregat"
								: "/guide/sauna-heater-sizing"
						}
						className="landing-guide-link"
					>
						<span className="landing-guide-title">
							{t.landing_guideHeaterTitle}
						</span>
						<span className="landing-guide-blurb">
							{t.landing_guideHeaterBlurb}
						</span>
					</Link>
				</div>
				<p className="landing-guides-all">
					<Link to="/guides">{t.landing_guidesAll}</Link>
				</p>
			</section>

			<section className="landing-faq" aria-labelledby="faq-heading">
				<h2 id="faq-heading" className="sky-h2">
					{t.landing_faqTitle}
				</h2>
				<details>
					<summary>{t.landing_faq1Q}</summary>
					<p>{costAnswer}</p>
				</details>
				<details>
					<summary>{t.landing_faq2Q}</summary>
					<p>{t.landing_faq2A}</p>
				</details>
				<details>
					<summary>{t.landing_faq3Q}</summary>
					<p>{t.landing_faq3A}</p>
				</details>
				<details>
					<summary>{t.landing_faq4Q}</summary>
					<p>{t.landing_faq4A}</p>
				</details>
				<details>
					<summary>{t.landing_faq5Q}</summary>
					<p>{t.landing_faq5A}</p>
				</details>
			</section>

			<StickyMobileCTA
				to={showDashboardCta ? "/dashboard" : "/wizard"}
				label={showDashboardCta ? t.landing_goToProjects : t.landing_primaryCta}
			/>

			<JsonLdScript value={faqJsonLd} />
			<JsonLdScript value={productJsonLd} />
		</div>
	);
}
